import React, { useState } from 'react';
import { MapPin, Calendar, Users, Send, User, Phone, Mail, CheckCircle2, Car, CreditCard } from 'lucide-react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { AddressAutocomplete } from '../components/AddressAutocomplete';
import { countryCodes } from '../data/countryCodes';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const emptyForm = {
  first_name: '',
  last_name: '',
  dial_code: '+33',
  phone_number: '',
  email: '',
  pickup_address: '',
  destination_address: '',
  date_time: '',
  passengers: 1,
  payment_method: 'Espèces',
  vehicle_type: 'Berline',
  message: ''
};

export const BookingForm = ({ compact = false }) => {
  const [formData, setFormData] = useState(emptyForm);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const { dial_code, phone_number, ...rest } = formData;
      const payload = {
        ...rest,
        phone: `${dial_code} ${phone_number}`.trim(),
        passengers: parseInt(formData.passengers, 10) || 1
      };
      await axios.post(`${BACKEND_URL}/api/bookings`, payload);
      if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
        window.gtag('event', 'reservation', {
          event_category: 'conversion',
          event_label: 'Réservation en ligne',
          vehicle_type: formData.vehicle_type,
          payment_method: formData.payment_method
        });
      }
      setSuccess(true);
      toast.success('Réservation envoyée !', {
        description: 'Nous vous recontactons dans les plus brefs délais pour confirmer votre course.'
      });
      setFormData(emptyForm);
    } catch (error) {
      if (process.env.NODE_ENV === 'development') console.error('Error submitting booking:', error);
      toast.error('Erreur', {
        description: "Une erreur est survenue. Veuillez réessayer ou nous appeler."
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (success) {
    return (
      <div className="text-center py-12" data-testid="booking-success">
        <div className="w-16 h-16 bg-amber-600/10 rounded-full flex items-center justify-center mx-auto mb-6">
          <CheckCircle2 className="w-9 h-9 text-amber-500" />
        </div>
        <h3 className="text-2xl font-bold text-white mb-3">Merci pour votre réservation</h3>
        <p className="text-gray-400 mb-8 max-w-md mx-auto">
          Votre demande a bien été transmise. Nous vous contacterons rapidement pour confirmer votre course.
        </p>
        <Button
          onClick={() => setSuccess(false)}
          className="bg-amber-600 hover:bg-amber-700 text-white"
          data-testid="booking-new-btn"
        >
          Faire une nouvelle réservation
        </Button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6" data-testid="booking-form">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="space-y-2">
          <Label htmlFor="first_name" className="text-white flex items-center gap-2"><User className="w-4 h-4 text-amber-500" />Prénom *</Label>
          <Input id="first_name" name="first_name" value={formData.first_name} onChange={handleChange} required
            className="bg-slate-950 border-slate-700 text-white focus:border-amber-500" placeholder="Jean" data-testid="booking-first-name" />
        </div>
        <div className="space-y-2">
          <Label htmlFor="last_name" className="text-white flex items-center gap-2"><User className="w-4 h-4 text-amber-500" />Nom *</Label>
          <Input id="last_name" name="last_name" value={formData.last_name} onChange={handleChange} required
            className="bg-slate-950 border-slate-700 text-white focus:border-amber-500" placeholder="Dupont" data-testid="booking-last-name" />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="space-y-2">
          <Label htmlFor="phone_number" className="text-white flex items-center gap-2"><Phone className="w-4 h-4 text-amber-500" />Téléphone *</Label>
          <div className="flex gap-2">
            <select
              name="dial_code"
              value={formData.dial_code}
              onChange={handleChange}
              className="w-32 flex-shrink-0 bg-slate-950 border border-slate-700 text-white rounded-md px-2 h-10 focus:border-amber-500 focus:outline-none text-sm"
              data-testid="booking-dial-code"
              aria-label="Indicatif pays"
            >
              {countryCodes.map((c) => (
                <option key={c.iso} value={c.dial}>{c.flag} {c.dial}</option>
              ))}
            </select>
            <Input id="phone_number" name="phone_number" type="tel" value={formData.phone_number} onChange={handleChange} required
              className="bg-slate-950 border-slate-700 text-white focus:border-amber-500" placeholder="6 12 34 56 78" data-testid="booking-phone" />
          </div>
        </div>
        <div className="space-y-2">
          <Label htmlFor="email" className="text-white flex items-center gap-2"><Mail className="w-4 h-4 text-amber-500" />Email *</Label>
          <Input id="email" name="email" type="email" value={formData.email} onChange={handleChange} required
            className="bg-slate-950 border-slate-700 text-white focus:border-amber-500" placeholder="jean@exemple.fr" data-testid="booking-email" />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="space-y-2">
          <Label className="text-white flex items-center gap-2"><MapPin className="w-4 h-4 text-amber-500" />Adresse de prise en charge *</Label>
          <AddressAutocomplete
            value={formData.pickup_address}
            onChange={(v) => setFormData((prev) => ({ ...prev, pickup_address: v }))}
            required
            placeholder="Aéroport Nice Côte d'Azur"
            testId="booking-pickup"
          />
        </div>
        <div className="space-y-2">
          <Label className="text-white flex items-center gap-2"><MapPin className="w-4 h-4 text-amber-500" />Adresse de destination *</Label>
          <AddressAutocomplete
            value={formData.destination_address}
            onChange={(v) => setFormData((prev) => ({ ...prev, destination_address: v }))}
            required
            placeholder="Monaco, Casino de Monte-Carlo"
            testId="booking-destination"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="space-y-2">
          <Label htmlFor="date_time" className="text-white flex items-center gap-2"><Calendar className="w-4 h-4 text-amber-500" />Date & heure souhaitées *</Label>
          <Input id="date_time" name="date_time" type="datetime-local" value={formData.date_time} onChange={handleChange} required
            className="bg-slate-950 border-slate-700 text-white focus:border-amber-500 [color-scheme:dark]" data-testid="booking-datetime" />
        </div>
        <div className="space-y-2">
          <Label htmlFor="passengers" className="text-white flex items-center gap-2"><Users className="w-4 h-4 text-amber-500" />Nombre de passagers *</Label>
          <Input id="passengers" name="passengers" type="number" min="1" max="20" value={formData.passengers} onChange={handleChange} required
            className="bg-slate-950 border-slate-700 text-white focus:border-amber-500" data-testid="booking-passengers" />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="space-y-2">
          <Label htmlFor="vehicle_type" className="text-white flex items-center gap-2"><Car className="w-4 h-4 text-amber-500" />Type de véhicule *</Label>
          <select id="vehicle_type" name="vehicle_type" value={formData.vehicle_type} onChange={handleChange} required
            className="w-full bg-slate-950 border border-slate-700 text-white rounded-md px-3 py-2 h-10 focus:border-amber-500 focus:outline-none" data-testid="booking-vehicle">
            <option value="Berline">Berline</option>
            <option value="Van">Van</option>
          </select>
        </div>
        <div className="space-y-2">
          <Label htmlFor="payment_method" className="text-white flex items-center gap-2"><CreditCard className="w-4 h-4 text-amber-500" />Mode de paiement *</Label>
          <select id="payment_method" name="payment_method" value={formData.payment_method} onChange={handleChange} required
            className="w-full bg-slate-950 border border-slate-700 text-white rounded-md px-3 py-2 h-10 focus:border-amber-500 focus:outline-none" data-testid="booking-payment">
            <option value="Espèces">Espèces</option>
            <option value="Carte bancaire">Carte bancaire</option>
          </select>
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="message" className="text-white">Précisions (bagages, siège enfant, etc.)</Label>
        <Textarea id="message" name="message" value={formData.message} onChange={handleChange} rows={compact ? 3 : 4}
          className="bg-slate-950 border-slate-700 text-white focus:border-amber-500 resize-none"
          placeholder="Informations complémentaires..." data-testid="booking-message" />
      </div>

      <Button type="submit" disabled={isSubmitting}
        className="w-full bg-amber-600 hover:bg-amber-700 text-white py-6 text-lg transition-all duration-300 hover:shadow-lg hover:shadow-amber-600/50 disabled:opacity-50 disabled:cursor-not-allowed"
        data-testid="booking-submit-btn">
        <Send className="w-5 h-5 mr-2" />
        {isSubmitting ? 'Envoi en cours...' : 'Réserver ma course'}
      </Button>
      <p className="text-gray-400 text-sm text-center">Réponse rapide — service disponible 7j/7, 24h/24</p>
    </form>
  );
};

const Reservation = () => {
  return (
    <div className="min-h-screen bg-slate-950 pt-20">
      <section className="py-20 bg-gradient-to-b from-slate-900 to-slate-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-block px-4 py-2 bg-amber-600/10 border border-amber-600/30 rounded-full mb-6">
            <span className="text-amber-500 text-sm font-medium">Réservation en ligne</span>
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6">Réservez Votre Course</h1>
          <p className="text-base md:text-lg text-gray-400 max-w-2xl mx-auto">
            Remplissez le formulaire ci-dessous, nous confirmons votre trajet dans les plus brefs délais.
          </p>
        </div>
      </section>

      <section className="py-16">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <Card className="bg-slate-900 border-slate-800">
            <CardContent className="p-6 sm:p-8">
              <BookingForm />
            </CardContent>
          </Card>
        </div>
      </section>
    </div>
  );
};

export default Reservation;
